import { useContext } from "react";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import {
  TextField,
  Button,
  EyeOnIcon,
  EyeOffIcon,
  PasswordRequirementList,
  getPasswordRequirements,
  PasswordStrengthMeter,
} from "@fmc-digital/ui";
import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import { register } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/customer";
import { useTranslation } from "react-i18next";
import {
  extractApiErrorCode,
  handleRegisterPersonalInfoMismatchError,
} from "@profile/features/registration/registerErrorMessage";
import {
  getPasswordSubmitValidationError,
  trackAccountCreateAnalytics,
} from "@profile/features/registration/passwordStep.utils";
import { useRegistrationProvider } from "@profile/providers/RegistrationProvider";
import { isAssumptionLoanAccountNumber } from "@profile/features/registration/assumption-loan.utils";


interface PasswordStepProps {
  setStatusMessage: (msg: string | null) => void;
}

export function PasswordStep({ setStatusMessage }: PasswordStepProps) {
  const { t, i18n } = useTranslation();
  const { trackRegistrationEvent, syncAnalyticsUserUUID } = useRegistrationProvider();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    goNext,
    email,
    personalInfo,
    otpId,
    otpFactorId,
    setPersonalInfoSubstep,
    setUserUUID,
  } = useContext(RegisterContext);
  const form = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      password: password || "",
      confirmPassword: confirmPassword || "",
    },
  });
  const { handleSubmit, formState, watch, control } = form;
  const values = watch();

  const toBase64 = (value: string): string => {
    if (typeof window === "undefined" || typeof window.btoa !== "function") {
      return value;
    }

    try {
      const utf8Bytes = new TextEncoder().encode(value);
      const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join("");
      return window.btoa(binaryString);
    } catch {
      return value;
    }
  };



  const getRegisterErrorMessage = (err: unknown): string => {
    const fallback = t("errors.genericError", "Something went wrong. Please try again.");
    const error = err as {
      message?: string;
      response?: {
        data?: {
          errorDetails?:
            | { errorCode?: string; code?: string; message?: string }
            | Array<{ errorCode?: string; code?: string; message?: string }>;
        };
      };
    };

    const details = error.response?.data?.errorDetails;
    const detail = Array.isArray(details) ? details[0] : details;
    const errorCode = detail?.errorCode || detail?.code;
    const errorMessage = detail?.message;

    if (errorCode) {
      const key = `errorCodes.${errorCode}`;
      if (i18n.exists(key)) {
        return t(key as never) as string;
      }
    }

    if (errorMessage && errorMessage.trim()) {
      return errorMessage;
    }

    if (error.message && error.message.trim()) {
      return error.message;
    }

    return fallback;
  };

  const onSubmit = async (data: { password: string; confirmPassword: string }) => {
    setStatusMessage(null);

    const validationError = getPasswordSubmitValidationError(
      data,
      personalInfo,
      otpId,
      otpFactorId,
      t("errors.required", "This field is required"),
    );
    if (validationError) {
      setStatusMessage(validationError);
      return;
    }

    const normalizedSsn = personalInfo.ssn.trim();
    const normalizedAccountNumber = personalInfo.loanNumber.trim();
    const isAssumptionLoan = isAssumptionLoanAccountNumber(normalizedAccountNumber);

    setLoading(true);
    try {
      const payload = {
        id: otpId.trim(),
        factorId: otpFactorId.trim(),
        lastName: personalInfo.lastName.trim(),
        ssn: normalizedSsn,
        accountNumber: normalizedAccountNumber,
        password: toBase64(data.password),
      };

      const response = await register(payload);
      setUserUUID(response.userUUID);
      syncAnalyticsUserUUID?.(response.userUUID);

      setPassword(data.password);
      setConfirmPassword(data.confirmPassword);
      trackAccountCreateAnalytics(trackRegistrationEvent, isAssumptionLoan, "success");
      goNext();
    } catch (err) {
      const message = getRegisterErrorMessage(err);
      const errorCode = extractApiErrorCode(err);
      trackAccountCreateAnalytics(trackRegistrationEvent, isAssumptionLoan, "failed", {
        ...(errorCode ? { errorCode } : {}),
        ...(message ? { description: message } : {}),
      });
      const handled = handleRegisterPersonalInfoMismatchError(
        errorCode,
        message,
        setPersonalInfoSubstep,
        setStatusMessage,
      );
      if (!handled) {
        setStatusMessage(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const requirements = getPasswordRequirements(email, personalInfo?.lastName);
  const metCount = requirements.filter(r => r.validator(values.password ?? "")).length;
  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="password"
        control={control}
        rules={{ required: "Password is required" }}
        render={({ field, fieldState }) => (
          <>
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={field.value ?? ""}
              onChange={e => {
                field.onChange(e);
                setPassword(e.target.value);
              }}
              onBlur={field.onBlur}
              fullWidth
              disabled={loading}
              error={fieldState.error?.message}
              endIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{ background: "none", border: 0, padding: 0, margin: 0, cursor: "pointer" }}
                >
                  {showPassword ? <EyeOnIcon /> : <EyeOffIcon />}
                </button>
              }
            />
            <PasswordStrengthMeter metCount={metCount} total={requirements.length} />
          </>
        )}
      />
            <PasswordRequirementList
        password={values.password}
        email={email}
        lastName={personalInfo?.lastName}
      />
      <Controller
        name="confirmPassword"
        control={control}
        rules={{
          required: "Confirm Password is required",
          validate: value => value === values.password || "Passwords do not match",
        }}
        render={({ field, fieldState }) => (
          <TextField
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            value={field.value ?? ""}
            onChange={e => {
              field.onChange(e);
              setConfirmPassword(e.target.value);
            }}
            onBlur={field.onBlur}
            fullWidth
            disabled={loading}
            error={fieldState.error?.message}
            endIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                style={{ background: "none", border: 0, padding: 0, margin: 0, cursor: "pointer" }}
              >
                {showConfirmPassword ? <EyeOnIcon /> : <EyeOffIcon />}
              </button>
            }
          />
        )}
      />

      <Button
        variant="filled"
        fullWidth
        type="submit"
        disabled={!formState.isValid || metCount !== requirements.length || loading}
      >
        {loading ? "Loading..." : "Next"}
      </Button>
      </form>
  );
}
